import { useMemo } from "react";
import { models } from "jiffy-api";
import { useImagesFastCache, type CachedImage } from "./useImagesFastCache";

type ArtworkEnhancedImagesResult = {
  print_original: CachedImage | undefined;
  custom: CachedImage | undefined;
  full_enhancements: CachedImage | undefined;
};

/* eslint-disable max-params */
export function useArtworkEnhancedImages(
  artwork: models.cable.DtfChannel.fetchDeliveryDate.Artwork | undefined,
  removeBackground: boolean,
  enhancementTier?: models.cart.madeAdd.EnhancementTier,
  canRemoveBackground: boolean = true
): ArtworkEnhancedImagesResult {
  const [noBgUrl] = useImagesFastCache(artwork && [artwork.noBgUrl || artwork.noBgNoTrimUrl]);

  const [originalImage, withBgUrl] = useImagesFastCache(
    artwork ? [artwork.originalImageUrl, artwork.withBgUrl] : []
  );

  const [manualEditImage] = useImagesFastCache(
    artwork?.manualEditUrl ? [artwork.manualEditUrl] : []
  );

  return useMemo(() => {
    const result: ArtworkEnhancedImagesResult = {
      print_original: undefined,
      custom: undefined,
      full_enhancements: undefined
    };

    if (originalImage?.base64) {
      result.print_original = originalImage;
      result.custom = originalImage;
    }

    // When a manual edit URL exists, always use it regardless of tier
    if (manualEditImage?.base64) {
      result.print_original = manualEditImage;
      result.custom = manualEditImage;
      result.full_enhancements = manualEditImage;
      return result;
    }

    if (enhancementTier === "print_original") {
      result.full_enhancements = originalImage;
    } else if (removeBackground && canRemoveBackground && noBgUrl) {
      result.full_enhancements = noBgUrl;
    } else if (withBgUrl) {
      result.full_enhancements = withBgUrl;
    } else {
      result.full_enhancements = originalImage;
    }

    return result;
  }, [
    noBgUrl,
    originalImage,
    removeBackground,
    withBgUrl,
    canRemoveBackground,
    manualEditImage,
    enhancementTier
  ]);
}
/* eslint-enable max-params */
